import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Input, LoadingButton, DialogShell, DialogHeader, DialogFooter, dialogButtonClass } from '@redpanda/ui';
import { getMemberRates, setMemberRate } from '../services/api';
import { useMembers } from '../stores/lookupStore';
import { notifyDataChanged } from '../utils/dataEvents';

/**
 * 成員每小時成本（管理員）：專案毛利＝合約金額 − Σ 工時 × 費率
 * 只存有改過的；第一次設定費率時，這個人之前沒有費率的工時一併套用（後端處理）
 */
const MemberRatesDialog: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { t } = useTranslation(['translation', 'pages']);
  const members = useMembers();
  const [original, setOriginal] = useState<Record<string, string>>({});
  const [values, setValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getMemberRates()
      .then(({ data }) => {
        const map = Object.fromEntries(data.map(r => [r.userId, String(r.hourlyCost)]));
        setOriginal(map);
        setValues(map);
      })
      .catch(error => console.error('Failed to load member rates:', error));
  }, []);

  const changed = members.filter(m => (values[m.id] ?? '') !== (original[m.id] ?? '') && values[m.id] !== '');

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all(changed.map(m => setMemberRate(m.id, Number(values[m.id]))));
      toast.success(t('common.messages.updateSuccess'));
      notifyDataChanged('timeEntries'); // 成本欄、毛利重算
      onClose();
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('common.messages.operationFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogShell onClose={onClose} closeDisabled={saving} className="max-w-lg">
      <DialogHeader title={t('pmTime.rates.title', { ns: 'pages' })} onClose={onClose} closeDisabled={saving} closeLabel={t('common.actions.close')} />
      <div className="p-6">
        <p className="text-[13px] text-fg-secondary">{t('pmTime.rates.hint', { ns: 'pages' })}</p>
        {/* 換算範例：中小企業、工作室多半不知道每小時成本怎麼填 */}
        <p className="mt-1 mb-4 text-[13px] text-fg-secondary">{t('pmTime.rates.formula', { ns: 'pages' })}</p>
        <ul className="divide-y divide-line rounded-md border border-line">
          {members.map(member => (
            <li key={member.id} className="flex items-center gap-3 px-4 py-2">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-fg">{member.name || member.email}</div>
                {member.name && <div className="truncate text-[13px] text-fg-secondary">{member.email}</div>}
              </div>
              <div className="w-36 shrink-0">
                <Input
                  type="text"
                  inputMode="decimal"
                  aria-label={t('pmTime.rates.hourlyCost', { ns: 'pages' })}
                  value={values[member.id] ?? ''}
                  onChange={e => /^\d*\.?\d*$/.test(e.target.value) && setValues(v => ({ ...v, [member.id]: e.target.value }))}
                  placeholder={t('pmTime.rates.placeholder', { ns: 'pages' })}
                  className="text-right tabular-nums"
                />
              </div>
            </li>
          ))}
        </ul>
      </div>
      <DialogFooter>
        <button type="button" onClick={onClose} disabled={saving} className={dialogButtonClass.cancel}>
          {t('common.actions.cancel')}
        </button>
        <LoadingButton type="button" onClick={save} loading={saving} disabled={changed.length === 0}>
          {t('common.actions.save')}
        </LoadingButton>
      </DialogFooter>
    </DialogShell>
  );
};

export default MemberRatesDialog;
